import React, { useState, useEffect } from 'react'
import { Plus, Edit, Trash2, Search, Eye, BookOpen, Calendar, Upload } from 'lucide-react'
import { apiClient } from '../../../lib/api.ts'
import { useAuth } from '../../../hooks/useAuth'

const BlogManagement = () => {
  const { userProfile } = useAuth()
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingPost, setEditingPost] = useState(null)
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [uploadingImage, setUploadingImage] = useState(false)

  // Estado para seleção de político autor (agora apenas tag opcional)
  const [politicians, setPoliticians] = useState([])
  const [loadingPoliticians, setLoadingPoliticians] = useState(false)
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    cover_image_url: '',
    is_published: false,
    tags: '',
    politician_id: ''
  })

  useEffect(() => {
    fetchPosts()
    fetchPoliticians()
  }, [])

  const fetchPosts = async () => {
    try {
      setLoading(true)
      const response = await apiClient.get('/blog')
      setPosts(response.data.data || [])
    } catch (error) {
      console.error('Erro ao carregar posts:', error)
      setPosts([])
    } finally {
      setLoading(false)
    }
  }

  const fetchPoliticians = async () => {
    try {
      setLoadingPoliticians(true)
      const response = await apiClient.get('/politicians?limit=100')
      setPoliticians(response.data?.data || [])
    } catch (error) {
      console.error('Erro ao carregar políticos:', error)
      setPoliticians([])
    } finally {
      setLoadingPoliticians(false)
    }
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (file.type.startsWith('image/')) {
        setImageFile(file)
        const reader = new FileReader()
        reader.onload = (e) => setImagePreview(e.target.result)
        reader.readAsDataURL(file)
      } else {
        alert('Por favor, selecione apenas arquivos de imagem (JPG, PNG, etc.)')
      }
    }
  }

  const uploadBlogImage = async () => {
    if (!imageFile) return null
    
    try {
      setUploadingImage(true)
      const formData = new FormData()
      formData.append('image', imageFile)
      
      const response = await apiClient.post('/upload/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      return response.data?.data?.url
    } catch (error) {
      console.error('Erro ao fazer upload da imagem:', error)
      throw new Error('Falha no upload da imagem')
    } finally {
      setUploadingImage(false)
    }
  }

  const resolveAuthorId = async () => {
    try {
      const res = await apiClient.get('/users/profile')
      const id = res?.data?.id || res?.data?.userId || res?.data?.data?.id
      return id || userProfile?.id || null
    } catch (error) {
      console.warn('⚠️ Falha ao obter author_id do backend, usando supabase id:', error?.message || error)
      return userProfile?.id || null
    }
  }

  const makeSlug = (title) => {
    return (title || '')
      .toLowerCase()
      .replace(/[\W_]+/g, ' ')
      .trim()
      .replace(/\s+/g, '-')
      .slice(0, 120)
  }

  const makeExcerpt = (content) => {
    const text = (content || '').replace(/<[^>]+>/g, '').trim()
    return text.length > 200 ? text.slice(0, 200) + '…' : text
  }

  // Reseta formulário e fecha o modal
  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      cover_image_url: '',
      is_published: false,
      tags: '',
      politician_id: ''
    })
    setEditingPost(null)
    setShowAddModal(false)
    setImageFile(null)
    setImagePreview(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      let imageUrl = formData.cover_image_url

      // Upload da imagem se houver uma nova
      if (imageFile) {
        imageUrl = await uploadBlogImage()
      }

      // Validações básicas
      if (!userProfile?.id) {
        alert('Faça login como jornalista/admin para publicar.')
        return
      }
      if (!formData.title?.trim()) {
        alert('Título é obrigatório.')
        return
      }
      if (!formData.content?.trim()) {
        alert('Conteúdo é obrigatório.')
        return
      }

      // politician_id agora é opcional; publicação geral de blog não exige associação

      const authorId = await resolveAuthorId()
      if (!authorId) {
        alert('Não foi possível resolver o autor. Refaça o login e tente novamente.')
        return
      }

      const postData = {
        title: formData.title.trim(),
        content: formData.content,
        cover_image_url: imageUrl || '',
        is_published: !!formData.is_published,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(Boolean),
        author_id: authorId,
        slug: makeSlug(formData.title),
        excerpt: makeExcerpt(formData.content)
      }

      console.debug('🟨 Enviando postData:', postData)

      if (editingPost) {
        await apiClient.put(`/blog/${editingPost.id}`, postData)
      } else {
        await apiClient.post('/blog', postData)
      }
      fetchPosts()
      resetForm()
    } catch (error) {
      console.error('Erro ao salvar post:', error)
      const errData = error?.response?.data
      try {
        console.error('🟥 Resposta de erro da API:', JSON.stringify(errData))
      } catch {}
      const errMsg =
        (errData?.message || errData?.error || errData?.detail) ??
        (typeof errData === 'object' ? JSON.stringify(errData) : String(errData)) ??
        error?.message ??
        'Erro ao salvar post. Tente novamente.'
      alert(`Erro ao salvar post: ${errMsg}`)
    }
  }

  const startEdit = (post) => {
    setFormData({
      title: post.title || '',
      content: post.content || '',
      cover_image_url: post.cover_image_url || '',
      is_published: !!post.is_published,
      tags: Array.isArray(post.tags) ? post.tags.join(', ') : '',
      politician_id: ''
    })
    setEditingPost(post)
    setShowAddModal(true)
    setImagePreview(post.cover_image_url)
    setImageFile(null)
  }

  const filteredPosts = Array.isArray(posts) ? posts.filter(post => {
    const matchesSearch = post.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.content?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = !filterStatus || 
      (filterStatus === 'published' && post.is_published) ||
      (filterStatus === 'draft' && !post.is_published)
    return matchesSearch && matchesStatus
  }) : []

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gerenciar Blog</h1>
          <p className="text-gray-600">Gerencie os posts do Blog Progressista</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Novo Post
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por título ou conteúdo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="w-48">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todos os status</option>
              <option value="draft">Rascunho</option>
              <option value="published">Publicado</option>
            </select>
          </div>
        </div>
      </div>

      {/* Posts List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Post
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPosts.map((post) => (
                <tr key={post.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-12 w-12">
                        {post.cover_image_url ? (
                          <img
                            className="h-12 w-12 rounded-lg object-cover"
                            src={post.cover_image_url}
                            alt={post.title}
                          />
                        ) : (
                          <div className="h-12 w-12 rounded-lg bg-gray-300 flex items-center justify-center">
                            <BookOpen className="h-6 w-6 text-gray-600" />
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {post.title}
                        </div>
                        <div className="text-sm text-gray-500">
                          {post.content?.substring(0, 100)}...
                        </div>
                        {post.tags && post.tags.length > 0 && (
                          <div className="mt-1">
                            {post.tags.slice(0, 3).map((tag, index) => (
                              <span
                                key={index}
                                className="inline-block bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded mr-1"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => toggleStatus(post.id, post.is_published)}
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        post.is_published
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {post.is_published ? 'Publicado' : 'Rascunho'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                      {formatDate(post.created_at)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => startEdit(post)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(post.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredPosts.length === 0 && (
          <div className="text-center py-12">
            <BookOpen className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum post encontrado</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || filterStatus ? 'Tente ajustar os filtros.' : 'Comece criando um novo post.'}
            </p>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingPost ? 'Editar Post' : 'Novo Post'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Título</label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Conteúdo</label>
                  <textarea
                    rows={8}
                    required
                    value={formData.content}
                    onChange={(e) => setFormData({...formData, content: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Imagem Destacada</label>
                  
                  {/* Preview da imagem */}
                  {imagePreview && (
                    <div className="mb-3">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-32 h-32 object-cover rounded-lg border border-gray-300"
                      />
                    </div>
                  )}
                  
                  {/* Upload de arquivo */}
                  <div className="mb-3">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                      id="blog-image"
                    />
                    <label
                      htmlFor="blog-image"
                      className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {imagePreview ? 'Trocar Imagem' : 'Selecionar Imagem'}
                    </label>
                    <p className="text-xs text-gray-500 mt-1">JPG, PNG, GIF até 5MB</p>
                  </div>
                  
                  {/* URL alternativa */}
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Ou URL da Imagem</label>
                    <input
                      type="url"
                      value={formData.cover_image_url}
                      onChange={(e) => {
                        setFormData({...formData, cover_image_url: e.target.value})
                        setImagePreview(e.target.value)
                      }}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="https://exemplo.com/imagem.jpg"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <select
                      value={formData.is_published ? 'published' : 'draft'}
                      onChange={(e) => setFormData({...formData, is_published: e.target.value === 'published'})}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="draft">Rascunho</option>
                      <option value="published">Publicado</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Tags (separadas por vírgula)</label>
                    <input
                      type="text"
                      value={formData.tags}
                      onChange={(e) => setFormData({...formData, tags: e.target.value})}
                      placeholder="política, progressismo, brasil"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={uploadingImage}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {uploadingImage && <Upload className="w-4 h-4 animate-spin" />}
                    {uploadingImage ? 'Enviando...' : (editingPost ? 'Atualizar Post' : 'Criar Post')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BlogManagement